import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

function LevelBadge({ level }) {
  if (!level) return null;
  const cls = level === 'FF1' ? 'tr-badge tr-badge-ff1' : 'tr-badge tr-badge-ff2';
  return <span className={cls}>{level}</span>;
}

export default function SkillList() {
  const navigate = useNavigate();
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [chapter, setChapter] = useState('');
  const [chapters, setChapters] = useState([]);
  const debounceRef = useRef(null);

  const fetchSkills = useCallback(async (s, lvl, ch) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (s) params.set('search', s);
      if (lvl) params.set('level', lvl);
      if (ch) params.set('chapter', ch);
      const res = await fetch(`/api/public/skills?${params}`);
      if (!res.ok) throw new Error('Failed to load skills');
      const data = await res.json();
      setSkills(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchChapters = useCallback(async (lvl) => {
    try {
      const params = new URLSearchParams();
      if (lvl) params.set('level', lvl);
      const res = await fetch(`/api/public/skills/meta/chapters?${params}`);
      if (res.ok) {
        const data = await res.json();
        setChapters(data);
        if (chapter && !data.includes(Number(chapter))) setChapter('');
      }
    } catch {}
  }, [chapter]);

  useEffect(() => {
    fetchChapters(levelFilter);
  }, [levelFilter, fetchChapters]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSkills(search, levelFilter, chapter);
    }, search ? 250 : 0);
    return () => clearTimeout(debounceRef.current);
  }, [search, levelFilter, chapter, fetchSkills]);

  // Re-fetch when page regains focus (user returns from detail view)
  useEffect(() => {
    const onFocus = () => fetchSkills(search, levelFilter, chapter);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [search, levelFilter, chapter, fetchSkills]);

  const toggleLevel = (lvl) => {
    setLevelFilter(prev => prev === lvl ? '' : lvl);
    setChapter('');
  };

  return (
    <div className="tr-page">
      <div className="tr-filterbar">
        <div className="tr-search-wrap">
          <span className="tr-search-icon">🔍</span>
          <input
            type="search"
            className="tr-search"
            placeholder="Search skills…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
          />
        </div>
        <div className="tr-filter-row">
          <button
            className={`tr-chip${!levelFilter ? ' active-all' : ''}`}
            onClick={() => { setLevelFilter(''); setChapter(''); }}
          >
            All
          </button>
          <button
            className={`tr-chip${levelFilter === 'FF1' ? ' active-ff1' : ''}`}
            onClick={() => toggleLevel('FF1')}
          >
            FF1
          </button>
          <button
            className={`tr-chip${levelFilter === 'FF2' ? ' active-ff2' : ''}`}
            onClick={() => toggleLevel('FF2')}
          >
            FF2
          </button>
          {chapters.length > 0 && (
            <select
              className="tr-chapter-select"
              value={chapter}
              onChange={e => setChapter(e.target.value)}
              aria-label="Filter by chapter"
            >
              <option value="">All Chapters</option>
              {chapters.map(ch => (
                <option key={ch} value={ch}>Chapter {ch}</option>
              ))}
            </select>
          )}
        </div>
        {!loading && (
          <div className="tr-result-count">
            {skills.length === 0 ? 'No skills found' : `${skills.length} skill${skills.length !== 1 ? 's' : ''}`}
          </div>
        )}
      </div>

      {loading && (
        <div className="tr-state-box">
          <span className="tr-state-icon">⏳</span>
          Loading skills…
        </div>
      )}

      {error && !loading && (
        <div className="tr-state-box">
          <span className="tr-state-icon">⚠️</span>
          {error}
          <button className="tr-chip" style={{ marginTop: 8 }} onClick={() => fetchSkills(search, levelFilter, chapter)}>
            Retry
          </button>
        </div>
      )}

      {!loading && !error && skills.length === 0 && (
        <div className="tr-state-box">
          <span className="tr-state-icon">📋</span>
          No skills match your filters.
        </div>
      )}

      {!loading && !error && skills.length > 0 && (
        <div className="tr-skill-list">
          {skills.map(skill => (
            <button
              key={skill.id}
              className="tr-skill-card"
              onClick={() => navigate(`/training/skill/${skill.id}`)}
            >
              <div className="tr-skill-card-info">
                <div className="tr-skill-card-name">{skill.name}</div>
                <div className="tr-skill-card-meta">
                  {skill.level && <LevelBadge level={skill.level} />}
                  {skill.chapter && <span>Ch. {skill.chapter}</span>}
                  {skill.checklist_items?.length > 0 && (
                    <span>{skill.checklist_items.length} step{skill.checklist_items.length !== 1 ? 's' : ''}</span>
                  )}
                </div>
              </div>
              <span className="tr-skill-card-chevron" aria-hidden="true">›</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
